import { Delete } from "@mui/icons-material";
import { Chip, ChipProps } from "@mui/material";
import { GridRowId, GridRowModel } from "@mui/x-data-grid-pro";
import { GridBulkActionsItem } from "components/DataGrid/components/GridBulkActionsItem";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import { useGlobalAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import _ from "lodash";
import { PageBreadcrumbs, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { useCallback } from "react";
import { useRouteLoaderData } from "react-router-dom";
import { api } from "services/api.service";
import { Pickup } from "types/pickup";
import { PickupContainer } from "types/pickupContainer";
import { pluralize } from "utils/helpers";
import { readableDate } from "utils/readableDate";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConfirmOptions = (handler: any, message?: string, title?: string, context?: string) => void;

const colorStateMap = {
  created: "primary",
  completed: "success",
  deleted: "error",
};

function PickupContainerStateChips({ state }: { state: string }) {
  return (
    <Chip
      label={_.startCase(state)}
      color={(colorStateMap[state as keyof typeof colorStateMap] || "primary") as ChipProps["color"]}
    />
  );
}

function BulkDeleteAction({ confirm }: { confirm: ConfirmOptions }) {
  const handleBulkDelete = (selectedRows: Map<GridRowId, GridRowModel>) => {
    const containers = Array.from(selectedRows.values()) as PickupContainer[];
    const numContainers = pluralize(containers.length, "container", "containers");
    const deletedContainers = containers.filter((container) => container.state === "deleted");
    const numDeletedContainers = pluralize(deletedContainers.length, "container", "containers");

    confirm({
      handler: () =>
        api
          .delete("containers/bulk", { data: { containers: containers } })
          .then(() => useGlobalAlert.alertSuccess(`${numContainers} deleted.`))
          .catch((error) => useGlobalAlert.alertError(`Failed to delete containers: Status ${error.statusText}`)),
      message: `Are you sure you want to delete ${numContainers}?
      ${deletedContainers.length > 0 ? `(${numDeletedContainers} of selected rows are already deleted)` : ""}`,
    });
  };

  return <GridBulkActionsItem icon={<Delete />} label="Delete" onClick={handleBulkDelete} />;
}

export default function ContainersPage() {
  const { pickup } = useRouteLoaderData("pickup") as { pickup: Pickup };
  const { confirm } = useConfirmation();
  const getBulkActions = useCallback(() => [<BulkDeleteAction confirm={confirm} />], [confirm]);

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs
        links={[
          { label: "Warehouse" },
          { label: "Pickups", path: "/pickups" },
          { label: `Pickup ${pickup.id}`, path: `/pickups/${pickup.uuid}` },
        ]}
      />
      <PageTitle>Container List</PageTitle>

      <RevivnDatabaseDataGrid
        checkboxSelection
        url={`pickups/${pickup.uuid}/containers`}
        getBulkActions={getBulkActions}
        columns={[
          { field: "id", headerName: "ID", type: "string" },
          { field: "label", headerName: "Label", minWidth: 150 },
          { field: "warehouseId", headerName: "Warehouse", valueGetter: (params) => params.row.warehouse.name },
          { field: "containerType", headerName: "Type", valueFormatter: (params) => _.capitalize(params.value) },
          {
            field: "warehouseLocation",
            headerName: "Location",
            minWidth: 150,
            renderCell: ({ row }) => (
              <WarehouseLocationLink code={row.warehouseLocation} locationId={row.locationManagementLocationId} />
            ),
          },
          {
            field: "state",
            headerName: "State",
            renderCell: (params) => <PickupContainerStateChips state={params.value} />,
            minWidth: 180,
          },
          { field: "imageCount", headerName: "Img Count" },
          { field: "createdAt", headerName: "Created At", valueFormatter: (params) => readableDate(params.value) },
        ]}
      />
    </FullPagePaperLayout>
  );
}
